import * as Sentry from '@sentry/nextjs';
import { useQuery } from '@tanstack/react-query';

import { ticketRequester } from 'queries/requesters';
import { StaleTime } from 'queries/types';
import { CartDocument } from 'types/api/tickets.types';

import { ticketsCartQuery } from './queries';
import { AppSyncTicketsGraphQLError } from './types';
import { isNotFound, useTicketingCartId, useTicketingEventId } from './utils';

export const useTicketsCart = () => {
  const { cartId, setCartId } = useTicketingCartId();
  const { eventId } = useTicketingEventId();
  const enabled = !!cartId && !!eventId;

  const { data, isLoading, isFetching, isPending, isPaused, status } = useQuery(
    {
      ...ticketsCartQuery({ cartId, eventId }),
      queryFn: async ({ signal }) => {
        try {
          // Ensure to await the response so we can catch the error
          const response = await ticketRequester(CartDocument, {
            cartId,
            eventId,
            signal,
          })();
          return response;
        } catch (error) {
          if (isNotFound(error as AppSyncTicketsGraphQLError)) {
            setCartId('');
          } else {
            Sentry.captureException(error);
          }
          throw error;
        }
      },
      staleTime: StaleTime.THIRTY_SEC,
      retry: false,
      refetchOnWindowFocus: 'always',
      enabled,
    },
  );

  return {
    cart: data?.cart,
    isLoading: (isPending && !isPaused && enabled) || (isLoading && isFetching),
    status,
  };
};
